import { mdiArrowLeft, mdiMinus, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  TabContent,
  Modal,
  ModalBody,
} from 'reactstrap';
import {
  addNewHeaders,
  getHeaders,
  addQaDetails,
  getQaDetails,
  updateHeaders,
  updateQaDetails,
  deleteHeaders,
  deleteQaDetails,
} from '../../store/apis/faqService';
import './FAQ.scss';
import NoResultImg from '../../assets/images/FAQ/No_Results_Found.png';
import {
  NewHeader,
  NewQuestion,
  EditHeader,
  EditQuestion,
  DeleteHeader,
  DeleteQuestion,
} from '../../components/FAQ';
import { getAuthUser } from '../../utils/auth.util';
import { ROLE_NAMES } from '../../common/constant';
import * as icons from '../../assets';

function FAQ() {
  document.title = 'FAQs | Envision Daikin';

  const [openQuestions, setOpenQuestions] = useState([]);
  const [newHeaderModal, setNewHeaderModal] = useState(false);
  const [newQuestionModal, setNewQuestionModal] = useState(false);
  const [deleteHeaderModal, setDeleteHeaderModal] = useState(false);
  const [deleteQuestionModal, setDeleteQuestionModal] = useState(false);
  const [editHeaderModal, setEditHeaderModal] = useState(false);
  const [editQuestionModal, setEditQuestionModal] = useState(false);
  const [headerToEdit, setHeaderToEdit] = useState(null);
  const [questionToEdit, setQuestionToEdit] = useState(null);
  const [headerToDelete, setHeaderToDelete] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [selectedHeaderId, setSelectedHeaderId] = useState(null);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    header: '',
    message: '',
  });

  const queryClient = useQueryClient();
  const headerRefs = useRef({});
  const newlyAddedHeaderIdRef = useRef(null);

  // Get Header Data
  const { data: headersData } = useQuery({
    queryKey: ['getHeaders'],
    queryFn: () =>
      getHeaders({
        isDeleted: 0,
      }),
    enabled: true,
  });

  // Get Active Tab
  const [activeTab, setActiveTab] = useState(null);

  // Set initial activeTab when headersData becomes available
  useEffect(() => {
    if (headersData?.length > 0) {
      const newId = newlyAddedHeaderIdRef.current;
      if (newId && headersData.some(h => h.HeaderId === newId)) {
        setActiveTab(newId);
        setSelectedHeaderId(newId);
        newlyAddedHeaderIdRef.current = null;
      } else if (!activeTab) {
        // Select the first header by default
        const firstHeaderId = headersData[0].HeaderId;
        setActiveTab(firstHeaderId);
        setSelectedHeaderId(firstHeaderId);
      }
    }
  }, [headersData]);

  useEffect(() => {
    if (selectedHeaderId && headerRefs.current[selectedHeaderId]) {
      headerRefs.current[selectedHeaderId].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      setSelectedHeaderId(null); // Reset after scroll
    }
  }, [selectedHeaderId]);

  // Get Q&A Details
  const { data: detailsData } = useQuery({
    queryKey: ['getQaDetails', activeTab],
    queryFn: () =>
      getQaDetails({
        headerId: activeTab,
        isDeleted: 0,
      }),
    enabled: !!activeTab,
  });

  // Toggles for Header and Question Modal
  const toggleNewHeaderModal = () => setNewHeaderModal(!newHeaderModal);
  const toggleNewQuestionModal = () => setNewQuestionModal(!newQuestionModal);

  // Toggle for Edit Header
  const toggleEditHeaderModal = header => {
    if (header) setHeaderToEdit(header);
    setEditHeaderModal(prev => !prev);
  };

  // Toggle for Edit Question
  const toggleEditQuestionModal = () => setEditQuestionModal(prev => !prev);

  // Toggle for Delete Header
  const toggleDeleteHeaderModal = () =>
    setDeleteHeaderModal(!deleteHeaderModal);

  const toggleAnswer = questionText => {
    if (openQuestions.includes(questionText)) {
      setOpenQuestions(prev => prev.filter(q => q !== questionText));
    } else {
      setOpenQuestions(prev => [...prev, questionText]);
    }
  };

  // Toggle for Delete Question
  const toggleDeleteQuestionModal = () =>
    setDeleteQuestionModal(!deleteQuestionModal);

  const openEditHeaderModal = header => {
    setHeaderToEdit(header);
    setEditHeaderModal(true);
  };

  const openDeleteHeaderModal = header => {
    setHeaderToDelete(header);
    toggleDeleteHeaderModal();
  };

  const openDeleteQuestionModal = question => {
    setQuestionToDelete(question);
    toggleDeleteQuestionModal();
  };

  // Create New Header Mutation
  const addHeaderMutation = useMutation({
    mutationFn: newHeader => {
      const user = getAuthUser();
      return addNewHeaders({
        ...newHeader,
        CreatedBy: user.userId,
      });
    },
    onSuccess: async data => {
      const headerId = data?.[0]?.v_headerId;
      newlyAddedHeaderIdRef.current = headerId;
      queryClient.invalidateQueries({ queryKey: ['getHeaders'] }); // Refresh header list
      toggleNewHeaderModal();
      setSuccessModal({
        visible: true,
        header: 'Header Added',
        message: 'New FAQs Header added successfully.',
      });
    },
    onError: () => {
      toast.error('Failed to add header', {
        className: 'toast-error',
      });
    },
  });

  // Create New Question Mutation
  const addQuestionMutation = useMutation({
    mutationFn: newQuestion => {
      const user = getAuthUser();
      return addQaDetails({
        ...newQuestion,
        CreatedBy: user.userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['getQaDetails', activeTab]);
      setNewQuestionModal(false); // close modal
      toast.success('New Question and Answer Added successfully', {
        style: { minWidth: '350px', whiteSpace: 'nowrap' },
        className: 'toast-success',
      });
    },
    onError: () => {
      toast.error('Failed to add question & answer', {
        className: 'toast-error',
      });
    },
  });

  // Update Header Mutation
  const updateHeaderMutation = useMutation({
    mutationFn: updateHeader => {
      const user = getAuthUser();
      return updateHeaders({
        ...updateHeader,
        CreatedBy: user.userId,
      });
    },
    onSuccess: (_data, variables) => {
      const updatedHeaderId = variables.headerId;
      queryClient.invalidateQueries({ queryKey: ['getHeaders'] }); // Refresh header list
      setActiveTab(updatedHeaderId); // If backend returns the newly created header ID
      setSelectedHeaderId(updatedHeaderId);
      setEditHeaderModal(false); // close modal.
      setSuccessModal({
        visible: true,
        header: 'Header Updated',
        message: 'Header updated successfully.',
      });
    },
    onError: () => {
      toast.error('Failed to update header', {
        className: 'toast-error',
      });
    },
  });

  // Update Question Mutation
  const updateQuestionMutation = useMutation({
    mutationFn: updateQuestion => {
      const user = getAuthUser();
      return updateQaDetails({
        ...updateQuestion,
        CreatedBy: user.userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getQaDetails', activeTab] }); // Refresh question list
      setEditQuestionModal(false); // close modal
      toast.success('Question and Answer Updated successfully.', {
        className: 'toast-success',
      });
    },
    onError: () => {
      toast.error('Failed to update question', {
        className: 'toast-error',
      });
    },
  });

  const deleteHeaderMutation = useMutation({
    mutationFn: headerId => {
      const user = getAuthUser();
      return deleteHeaders({
        headerId,
        CreatedBy: user.userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getHeaders'] }); // Refresh header list
      setActiveTab(null); // Reset active tab
      setDeleteHeaderModal(false); // close modal
      toast.success('Header Deleted successfully', {
        className: 'toast-success',
      });
    },
    onError: () => {
      toast.error('Failed to delete header', {
        className: 'toast-error',
      });
    },
  });

  const deleteQaDetailMutation = useMutation({
    mutationFn: ({ qaId, headerId }) => {
      const user = getAuthUser();
      return deleteQaDetails({
        qaId,
        headerId,
        CreatedBy: user.userId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getQaDetails', activeTab] }); // Refresh header list
      setDeleteQuestionModal(false); // close modal
      toast.success('Question and Answer Deleted successfully', {
        style: { minWidth: '350px', whiteSpace: 'nowrap' },
        className: 'toast-success',
      });
    },
    onError: () => {
      toast.error('Failed to delete Q&A', {
        className: 'toast-error',
      });
    },
  });

  const roleName = getAuthUser()?.role?.Name;

  const isAdmin = roleName === ROLE_NAMES.ADMIN;
  const isHR = roleName === ROLE_NAMES.HR;
  const isAdminOrHR = isAdmin || isHR;

  return (
    <div className="page-content">
      <div className="faq-header d-flex justify-content-between p-4 mb-3 gap-2">
        <div className="d-flex flex-row align-items-center gap-1">
          <div>
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => window.history.back()}
            >
              <Icon path={mdiArrowLeft} size={0.8} color="#017EBD" />
            </button>
          </div>
          <div className="fw-semibold" style={{ color: '#017EBD' }}>
            FAQs
          </div>
        </div>

        {isAdminOrHR && (
          <button
            onClick={toggleNewHeaderModal}
            type="button"
            className="custom-btn"
            style={{ backgroundColor: '#149CE0' }}
          >
            Add New FAQs Header
          </button>
        )}
      </div>
      <div className="d-flex flex-row">
        <Col lg={3}>
          <ul className="nav flex-column gap-2">
            {headersData?.length === 0 ? (
              <p className="text-muted px-2">No headers available.</p>
            ) : (
              headersData?.map(header => (
                <button
                  type="button"
                  ref={el => {
                    headerRefs.current[header?.HeaderId] = el;
                    return null;
                  }}
                  className={`faq-header-item ${activeTab === header?.HeaderId ? 'active' : ''}`}
                  key={header?.HeaderId}
                  onClick={() => setActiveTab(header?.HeaderId)}
                >
                  <span>{header?.Title}</span>
                  {isAdminOrHR && (
                    <div>
                      <button
                        title="Delete Header"
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => openDeleteHeaderModal(header)}
                      >
                        <img
                          src={icons?.iconDeleteFaq}
                          alt="Delete Header"
                          className="faq-icon-delete"
                        />
                      </button>
                      <button
                        title="Edit Header"
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => openEditHeaderModal(header)}
                      >
                        <img
                          src={icons?.iconEditFaq}
                          alt="Edit Header"
                          className="faq-icon-edit"
                        />
                      </button>
                    </div>
                  )}
                </button>
              ))
            )}
          </ul>
        </Col>
        <Col lg={9}>
          <TabContent activeTab={activeTab}>
            <Card>
              {isAdminOrHR && (
                <CardHeader className="faq-question d-flex p-4 mb-3">
                  <button
                    type="button"
                    className="custom-light-btn ms-auto"
                    onClick={toggleNewQuestionModal}
                    disabled={!activeTab}
                    style={{
                      opacity: !activeTab ? 0.6 : 1,
                      cursor: !activeTab ? 'not-allowed' : 'pointer',
                    }}
                    title={
                      !activeTab
                        ? 'Please select a header first'
                        : 'Add Question & Answers'
                    }
                  >
                    Add Question & Answers
                  </button>
                </CardHeader>
              )}

              <CardBody className="qna-card-body">
                {detailsData?.length === 0 ? (
                  <div className="image-container">
                    <img
                      src={NoResultImg} // or any path to your image
                      alt="No Results"
                    />
                  </div>
                ) : (
                  detailsData?.map(question => {
                    const isOpen = openQuestions.includes(question.Question);

                    return (
                      <div className="question-item" key={question['QA ID']}>
                        <div
                          className="faq-section-question cursor-pointer"
                          onClick={() => toggleAnswer(question.Question)}
                        >
                          <div className="faq-question">
                            {question.Question}
                          </div>
                          <div className="d-flex flex-row gap-2">
                            {isAdminOrHR && (
                              <div className="d-flex flex-row gap-2">
                                <button
                                  title="Delete Question"
                                  type="button"
                                  className="btn btn-link p-0"
                                  onClick={e => {
                                    e.stopPropagation();
                                    openDeleteQuestionModal(question);
                                  }}
                                >
                                  <img
                                    src={icons?.iconDeleteFaq}
                                    alt="Delete Header"
                                    className="faq-icon-delete"
                                  />
                                </button>
                                <button
                                  title="Edit Question"
                                  type="button"
                                  className="btn btn-link p-0"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setQuestionToEdit(question);
                                    toggleEditQuestionModal();
                                  }}
                                >
                                  <img
                                    src={icons?.iconEditFaq}
                                    alt="Edit Header"
                                    className="faq-icon-edit-qn"
                                  />
                                </button>
                              </div>
                            )}

                            <button
                              onClick={() => toggleAnswer(question.Question)}
                              type="button"
                              className="btn btn-link p-0"
                            >
                              {isOpen ? (
                                <Icon path={mdiMinus} size={0.8} />
                              ) : (
                                <Icon path={mdiPlus} size={0.8} />
                              )}
                            </button>
                          </div>
                        </div>
                        {isOpen && (
                          <div
                            className="faq-section-answer"
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(question.Answer, {
                                ADD_TAGS: ['iframe'],
                                ADD_ATTR: [
                                  'allow',
                                  'allowfullscreen',
                                  'frameborder',
                                  'scrolling',
                                ],
                              }),
                            }}
                          />
                        )}
                      </div>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </TabContent>
        </Col>
      </div>
      <NewQuestion
        isOpen={newQuestionModal}
        toggle={toggleNewQuestionModal}
        header={headersData?.find(h => h.HeaderId === activeTab)}
        addQuestionMutation={addQuestionMutation} // Pass mutation as prop
      />
      <NewHeader
        isOpen={newHeaderModal}
        toggle={toggleNewHeaderModal}
        onSubmit={addHeaderMutation.mutate}
      />
      <EditHeader
        isOpen={editHeaderModal}
        toggle={() => toggleEditHeaderModal()}
        header={headerToEdit}
        onSubmit={updateHeaderMutation.mutate}
      />
      <EditQuestion
        isOpen={editQuestionModal}
        toggle={toggleEditQuestionModal}
        qaDetails={questionToEdit}
        header={headersData?.find(h => h.HeaderId === activeTab)}
        onSubmit={updateQuestionMutation.mutate}
      />
      <DeleteHeader
        isOpen={deleteHeaderModal}
        toggle={toggleDeleteHeaderModal}
        header={headerToDelete?.Title} // or any property you want to display
        onConfirm={() => deleteHeaderMutation.mutate(headerToDelete?.HeaderId)}
      />
      <DeleteQuestion
        isOpen={deleteQuestionModal}
        toggle={toggleDeleteQuestionModal}
        qaDetails={questionToDelete}
        onConfirm={() =>
          deleteQaDetailMutation.mutate({
            qaId: questionToDelete?.['QA ID'],
            headerId: activeTab,
          })
        }
      />
      <SuccessModal
        isOpen={successModal.visible}
        toggle={() =>
          setSuccessModal({ visible: false, header: '', message: '' })
        }
        header={successModal.header}
        message={successModal.message}
      />
    </div>
  );
}

function SuccessModal({ isOpen, toggle, message, header }) {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="sm">
      <ModalBody className="text-center">
        <h5 className="success-header-title">{header}</h5>
        <p>{message}</p>
        <button
          type="button"
          className="success-header-close-btn w-100"
          onClick={toggle}
        >
          Close
        </button>
      </ModalBody>
    </Modal>
  );
}

export default FAQ;
